import React, { useEffect, useState, Suspense } from "react";
import PropTypes from "prop-types";
import ExecutionEnvironment from "exenv";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";

import { makeStyles } from "tss-react/mui";
import {
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Box,
  Container,
  Button,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";

// redux stuff
import { connect } from "react-redux";
import axios from "axios";

const useStyles = makeStyles()((theme) => ({
  fullPageHeight: {
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
  },
  heroSection: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    textAlign: "center",
  },
  heading: {
    marginBottom: theme.spacing(1),
  },
  subTitle: {
    fontSize: 20,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  loadingDiv: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  gridContainer: {
    padding: theme.spacing(2, 2),
  },
  partnerCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    transition: "all 0.3s ease",
    borderRadius: theme.spacing(2),
    overflow: "visible",
    "&:hover": {
      transform: "translateY(-12px)",
      boxShadow: theme.shadows[12],
    },
  },
  partnerCardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 300,
    padding: theme.spacing(1),
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
  partnerAvatar: {
    width: 140,
    height: 140,
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    fontSize: "3.5rem",
    fontWeight: "bold",
    border: `4px solid ${theme.palette.primary.light}`,
    boxShadow: theme.shadows[3],
  },
  partnerName: {
    fontWeight: "bold",
    fontSize: "1.25rem",
    marginBottom: theme.spacing(0.5),
    textAlign: "center",
  },
  partnerCompany: {
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    fontSize: "0.95rem",
  },
  partnerBio: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    textAlign: "center",
    flexGrow: 1,
    lineHeight: 1.5,
    fontSize: "0.9rem",
  },
  partnerWebsite: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  websiteButton: {
    borderRadius: theme.spacing(3),
    textTransform: "none",
    padding: theme.spacing(0.75, 2.5),
  },
  benefitsSection: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6, 2),
  },
  benefitsList: {
    maxWidth: 800,
    margin: "0 auto",
  },
  benefitItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  ctaSection: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    backgroundColor: theme.palette.action.hover,
  },
  ctaButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
    fontSize: "1.1rem",
  },
}));

function FoundingPartners(props) {
  const { classes } = useStyles();
  const [foundingPartners, setFoundingPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all accepted founding partners
    axios
      .get("/api/foundingPartners/getAcceptedPartners")
      .then((res) => {
        setFoundingPartners(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching founding partners:", err);
        setLoading(false);
      });
  }, []);

  function getInitials(firstName, lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title="Founding Partners - Content Generator"
        description="Meet the exclusive group of creators and industry leaders who are shaping the future of Content Generator as founding partners."
        url="https://contentgenerator.io/founding-partners"
      />

      {/* Hero Section */}
      <div className={classes.heroSection}>
        <Container maxWidth="md">
          <Typography variant="h1" className={classes.heading}>
            Our Founding Partners
          </Typography>
          <Typography className={classes.subTitle}>
            Meet the exclusive group of creators and industry leaders helping
            shape the future of Content Generator
          </Typography>
        </Container>
      </div>

      {/* Partners Grid */}
      <Container maxWidth="lg" className={classes.gridContainer}>
        {loading ? (
          <div className={classes.loadingDiv}>
            <CircularProgress size={40} />
            <Typography>Loading our founding partners...</Typography>
          </div>
        ) : foundingPartners.length === 0 ? (
          <div className={classes.loadingDiv}>
            <Typography variant="h3" gutterBottom>
              Our Founding Partners Are Being Onboarded
            </Typography>
            <Typography>
              Check back soon to see the amazing creators joining us!
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {foundingPartners.map((partner, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card className={classes.partnerCard} elevation={2}>
                  <CardContent className={classes.partnerCardContent}>
                    <Avatar
                      className={classes.partnerAvatar}
                      src={partner.profileImageUrl || ""}
                      alt={`${partner.firstName} ${partner.lastName}`}
                    >
                      {!partner.profileImageUrl &&
                        getInitials(partner.firstName, partner.lastName)}
                    </Avatar>

                    <Typography variant="h3" className={classes.partnerName}>
                      {partner.firstName} {partner.lastName}
                    </Typography>

                    {partner.company && (
                      <Typography
                        variant="body1"
                        className={classes.partnerCompany}
                      >
                        <BusinessIcon fontSize="small" />
                        {partner.company}
                      </Typography>
                    )}

                    {partner.bio && (
                      <Typography
                        variant="body2"
                        className={classes.partnerBio}
                      >
                        {partner.bio}
                      </Typography>
                    )}

                    {partner.websiteUrl && (
                      <Box className={classes.partnerWebsite}>
                        <Button
                          component="a"
                          href={partner.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          color="primary"
                          className={classes.websiteButton}
                          startIcon={<LanguageIcon />}
                        >
                          Visit Website
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Benefits Section */}
      <div className={classes.benefitsSection}>
        <Container maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            What Our Founding Partners Get
          </Typography>
          <Typography
            align="center"
            color="textSecondary"
            paragraph
            style={{ marginBottom: 32 }}
          >
            This exclusive program offers unmatched benefits and direct
            influence over the platform's development
          </Typography>

          <div className={classes.benefitsList}>
            <Box className={classes.benefitItem}>
              <Typography variant="h3" gutterBottom>
                💰 40% Lifetime Recurring Commissions
              </Typography>
              <Typography>
                Earn 40% on every signup and renewal, forever. That's 60% more
                than our standard affiliate program, and it's locked in for
                life.
              </Typography>
            </Box>

            <Box className={classes.benefitItem}>
              <Typography variant="h3" gutterBottom>
                🆓 Free Lifetime Access
              </Typography>
              <Typography>
                Full access to all Content Generator features at no cost, ever.
                This benefit is exclusive to founding partners and will never be
                offered publicly.
              </Typography>
            </Box>

            <Box className={classes.benefitItem}>
              <Typography variant="h3" gutterBottom>
                🔒 Private Product Council
              </Typography>
              <Typography>
                Direct input on features, roadmap, and product direction through
                an exclusive Discord channel. Vote on upcoming features and
                request what you need.
              </Typography>
            </Box>

            <Box className={classes.benefitItem}>
              <Typography variant="h3" gutterBottom>
                🎨 Co-Branded Partner Profile
              </Typography>
              <Typography>
                Your own branded page on Content Generator featuring your links,
                socials, template packs, and optional tutorials. Your brand
                becomes part of our platform.
              </Typography>
            </Box>

            <Box className={classes.benefitItem}>
              <Typography variant="h3" gutterBottom>
                🏆 Performance Bonuses
              </Typography>
              <Typography>
                Earn $250 at 20 subscribers, $750 at 50 subscribers, and $2,000
                at 100 subscribers. These bonuses are in addition to your
                recurring commissions.
              </Typography>
            </Box>
          </div>
        </Container>
      </div>

      {/* CTA Section */}
      <div className={classes.ctaSection}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom>
            Interested in Joining?
          </Typography>
          <Typography variant="body1" paragraph>
            The Founding Partners Program is invite-only. We're looking for
            creators and industry leaders who are actively publishing content
            and running real businesses in the social media marketing space.
          </Typography>
          <Typography variant="body1" paragraph>
            If you've received an invitation, use the link in your email to get
            started.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.ctaButton}
            href="/contact"
          >
            Contact Us
          </Button>
        </Container>
      </div>
    </div>
  );
}

FoundingPartners.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(FoundingPartners);
